import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

const OptionCard = styled.button`
  width: 100%;
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.offWhite : theme.colors.white};
  border: 2px solid
    ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.primary : theme.colors.offWhiteDark};
  border-radius: 12px;
  padding: 1.4rem 1.6rem;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;
  text-align: left;

  &:hover {
    background: ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.tanLight : theme.colors.offWhite};
    border-color: ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.primaryDark : theme.colors.tanDark};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const OptionLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 1.6rem;
`;

const OptionImage = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.offWhite};
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.offWhiteDark};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
  }
`;

const OptionInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const OptionName = styled.div`
  font-size: 1.6rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  line-height: 1.2;
  margin-bottom: 0.4rem;
`;

const OptionDescription = styled.div`
  font-size: 1.4rem;
  color: ${({ theme }) => theme.colors.black};
  opacity: 0.7;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;

const OptionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  flex-shrink: 0;
`;

const OptionPrice = styled.div`
  font-size: 1.6rem;
  font-weight: 700;
  color: ${({ $isPositive, theme }) =>
    $isPositive ? theme.colors.success : theme.colors.error};
`;

const SelectionIndicator = styled.div`
  width: 24px;
  height: 24px;
  border-radius: ${({ $isRadio }) => ($isRadio ? '50%' : '6px')};
  border: 2px solid
    ${({ $isSelected, theme }) =>
      $isSelected ? theme.colors.primary : theme.colors.tanDark};
  background: ${({ $isSelected, theme }) =>
    $isSelected ? theme.colors.primary : theme.colors.white};
  position: relative;
  transition: all 0.2s;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: ${({ $isRadio }) => ($isRadio ? '8px' : '12px')};
    height: ${({ $isRadio }) => ($isRadio ? '8px' : '8px')};
    background: ${({ $isRadio, theme }) =>
      $isRadio ? theme.colors.white : 'transparent'};
    border-radius: ${({ $isRadio }) => ($isRadio ? '50%' : '0')};
    border-left: ${({ $isRadio, theme }) =>
      $isRadio ? 'none' : `2px solid ${theme.colors.white}`};
    border-bottom: ${({ $isRadio, theme }) =>
      $isRadio ? 'none' : `2px solid ${theme.colors.white}`};
    transform: ${({ $isRadio, $isSelected }) =>
      $isRadio
        ? `translate(-50%, -50%) scale(${$isSelected ? 1 : 0})`
        : `translate(-50%, -60%) rotate(-45deg) scale(${$isSelected ? 1 : 0})`};
    transition: all 0.2s;
  }
`;

const formatPrice = (price) => {
  if (!price || price === 0) return null;
  const isPositive = price > 0;
  return {
    text: `${isPositive ? '+' : ''}$${Math.abs(price / 100).toFixed(2)}`,
    isPositive,
  };
};

export const ModalOption = ({
  modifier,
  isSelected,
  isRadio,
  onClick,
  disabled,
}) => {
  const priceInfo = formatPrice(modifier.price);

  return (
    <OptionCard
      $isSelected={isSelected}
      onClick={onClick}
      disabled={disabled}
      type="button"
    >
      <OptionLayout>
        {modifier.imageUrl && (
          <OptionImage>
            <img src={modifier.imageUrl} alt={modifier.name} />
          </OptionImage>
        )}

        <OptionInfo>
          <OptionName>{modifier.name}</OptionName>
          {modifier.description && (
            <OptionDescription>{modifier.description}</OptionDescription>
          )}
        </OptionInfo>

        <OptionMeta>
          {priceInfo && (
            <OptionPrice $isPositive={priceInfo.isPositive}>
              {priceInfo.text}
            </OptionPrice>
          )}
          <SelectionIndicator $isRadio={isRadio} $isSelected={isSelected} />
        </OptionMeta>
      </OptionLayout>
    </OptionCard>
  );
};

ModalOption.propTypes = {
  modifier: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    imageUrl: PropTypes.string,
    price: PropTypes.number,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  isRadio: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

export const ModalCompactOption = ModalOption;
export const ModalListOption = ModalOption;

export default ModalOption;
